import * as styles from "./AmendmentDialog.css";

import { useAtomValue, useSetAtom } from "jotai";
import { type JSX, type RefObject, useMemo, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  DatePicker,
  DatePickerContent,
  DatePickerTrigger,
  DatePickerValue,
} from "@frontend/components/atoms/DatePicker";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@frontend/components/atoms/Dialog";
import {
  Field,
  FieldContent,
  FieldContext,
  FieldError,
  FieldLabel,
  makeFieldContext,
} from "@frontend/components/atoms/Field";
import { Icon } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import { Textarea } from "@frontend/components/atoms/Textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import { useField } from "@frontend/hooks/form";
import { useDiscardGuard } from "@frontend/hooks/useDiscardGuard";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import type { InferFieldsFromSchema, ItemAtom } from "@frontend/lib/form";
import { DiscardChangesDialog } from "@frontend/shared/discard-changes-dialog";
import { formatAmount, formatPercent } from "@frontend/utils/amount";
import { getAmountMask } from "@frontend/utils/masks";
import { Trans, useLingui } from "@lingui/react/macro";

import type { AmendmentValues, amendmentSchema } from "../schemas/contract";
import {
  cancelAmendmentEditAtom,
  contractForm,
  editedAmendmentAtom,
} from "../stores/editor";
import { priceChange } from "../utils/amendment";

export type AmendmentFields = InferFieldsFromSchema<typeof amendmentSchema>;

const DIALOG_WIDTH = 560;

const gridFields = makeFieldContext({
  orientation: "vertical",
  grouped: false,
  errorDisplay: "tooltip",
});

export type AmendmentDialogProps = {
  index: number;
  fields: AmendmentFields;
  itemAtom: ItemAtom;
  previousPrice: string | null;
  created: boolean;
  onRemove: () => void;
  originRef?: RefObject<DOMRect | null>;
};

export function AmendmentDialog({
  index,
  fields,
  itemAtom,
  previousPrice,
  created,
  onRemove,
  originRef,
}: AmendmentDialogProps): JSX.Element {
  const { t } = useLingui();
  const locale = useIntlLocale();

  const errors = useAtomValue(contractForm.state.errors);

  const close = useSetAtom(editedAmendmentAtom);
  const cancelEdit = useSetAtom(cancelAmendmentEditAtom);
  const touch = useSetAtom(contractForm.actions.touch);

  const deletable = useAtomValue(fields.deletable.atom);

  const dateBegin = useField(fields.dateBegin);
  const price = useField(fields.price);
  const comments = useField(fields.comments);

  const current = useAtomValue(itemAtom) as AmendmentValues;

  const [snapshot] = useState(current);

  const dirty = JSON.stringify(current) !== JSON.stringify(snapshot);

  const guard = useDiscardGuard({
    dirty,
    onClose: () => cancelEdit(snapshot),
  });

  const failing = useMemo(() => {
    const prefix = `amendments.${index}.`;

    return Object.keys(errors).filter((path) => path.startsWith(prefix));
  }, [errors, index]);

  const confirm = () => {
    if (0 < failing.length) {
      for (const path of failing) {
        touch(path);
      }

      return;
    }

    close(null);
  };

  const position = index + 1;

  const change = priceChange(previousPrice, price.value ?? null);

  return (
    <Dialog open onOpenChange={guard.onOpenChange} originRef={originRef}>
      <DialogContent size={DIALOG_WIDTH}>
        <DialogHeader>
          <DialogTitle className={styles.title}>
            {created ? t`New amendment` : t`Amendment ${position}`}

            {change ? (
              <span className={styles.subtitle}>
                {`${formatPercent(change.percent, locale)} % ${t`vs`} ${formatAmount(change.previous, locale)} ${t`Frs`}`}
              </span>
            ) : null}
          </DialogTitle>

          <DialogClose />
        </DialogHeader>

        <div className={styles.body}>
          <FieldContext.Provider value={gridFields}>
            <div className={styles.grid}>
              <Field
                className={styles.span.half}
                invalid={Boolean(dateBegin.error)}
              >
                <FieldLabel>{t`Effective date`}</FieldLabel>

                <FieldContent>
                  <DatePicker
                    locale={locale}
                    value={dateBegin.value ?? null}
                    onValueChange={(next) => dateBegin.setValue(next)}
                  >
                    <DatePickerTrigger>
                      <DatePickerValue placeholder={t`Enter a date`} />
                    </DatePickerTrigger>

                    <DatePickerContent />
                  </DatePicker>

                  <FieldError match={Boolean(dateBegin.error)}>
                    {dateBegin.error}
                  </FieldError>
                </FieldContent>
              </Field>

              <Field
                className={styles.span.half}
                invalid={Boolean(price.error)}
              >
                <FieldLabel>{t`New price`}</FieldLabel>

                <FieldContent>
                  <Input
                    inputMode="decimal"
                    mask={getAmountMask()}
                    value={price.value ?? ""}
                    rightSection={
                      <span className={styles.suffix}>{t`Frs`}</span>
                    }
                    onValueChange={(next) =>
                      price.setValue("" === next ? null : next)
                    }
                  />

                  <FieldError match={Boolean(price.error)}>
                    {price.error}
                  </FieldError>
                </FieldContent>
              </Field>

              <Field
                className={styles.span.full}
                invalid={Boolean(comments.error)}
              >
                <FieldLabel>{t`Description`}</FieldLabel>

                <FieldContent>
                  <Textarea
                    className={styles.comments}
                    value={comments.value ?? ""}
                    placeholder={t`Reason for the amendment`}
                    onChange={(event) =>
                      comments.setValue(
                        "" === event.target.value ? null : event.target.value,
                      )
                    }
                  />

                  <FieldError match={Boolean(comments.error)}>
                    {comments.error}
                  </FieldError>
                </FieldContent>
              </Field>
            </div>
          </FieldContext.Provider>
        </div>

        <DialogFooter>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  className={styles.remove}
                  variant="ghost"
                  disabled={false === deletable}
                  aria-label={t`Remove this amendment`}
                  onClick={onRemove}
                >
                  <Icon name="Trash2" size="sm" />
                </Button>
              }
            />

            <TooltipContent>{t`Remove this amendment`}</TooltipContent>
          </Tooltip>

          <Button variant="neutral" onClick={guard.requestClose}>
            <Trans>Cancel</Trans>
          </Button>

          <Button variant="primary" onClick={confirm}>
            <Trans>Apply</Trans>
          </Button>
        </DialogFooter>

        <DiscardChangesDialog
          open={guard.confirming}
          onKeep={guard.keep}
          onDiscard={guard.discard}
        />
      </DialogContent>
    </Dialog>
  );
}
